export type GrowerTariffMode =
  | 'replace'
  | 'percentage-discount'
  | 'amount-discount';
export interface GrowerException {
  growers: string[];
  commissionPercentage: number | null;
  tariff: { mode: GrowerTariffMode; value: number } | null;
}

// Grower names are the producer identity used by both shipment sources and reports.
export const growerKey = (name: string | null | undefined) =>
  (name ?? '').trim().toUpperCase();
const nonnegative = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value >= 0;

// Reject malformed or overlapping rules rather than silently changing charges.
export function parseGrowerExceptions(
  value: unknown
): GrowerException[] | null {
  if (!Array.isArray(value)) return null;
  const seen = new Set<string>();
  const result: GrowerException[] = [];
  for (const rule of value) {
    if (
      !rule ||
      typeof rule !== 'object' ||
      !Array.isArray(rule.growers) ||
      !rule.growers.length
    )
      return null;
    for (const name of rule.growers) {
      if (
        typeof name !== 'string' ||
        !growerKey(name) ||
        seen.has(growerKey(name))
      )
        return null;
      seen.add(growerKey(name));
    }
    if (
      rule.commissionPercentage !== null &&
      (!nonnegative(rule.commissionPercentage) ||
        rule.commissionPercentage > 100)
    )
      return null;
    if (
      rule.tariff !== null &&
      (!rule.tariff ||
        !['replace', 'percentage-discount', 'amount-discount'].includes(
          rule.tariff.mode
        ) ||
        !nonnegative(rule.tariff.value) ||
        (rule.tariff.mode === 'percentage-discount' && rule.tariff.value > 100))
    )
      return null;
    if (rule.commissionPercentage === null && rule.tariff === null) return null;
    result.push({
      growers: [...rule.growers],
      commissionPercentage: rule.commissionPercentage,
      tariff:
        rule.tariff === null
          ? null
          : { mode: rule.tariff.mode, value: rule.tariff.value },
    });
  }
  return result;
}

export function createGrowerChargeResolver(
  rules: GrowerException[],
  defaultCommission: number
) {
  const byGrower = new Map<string, GrowerException>();
  // Incomplete editor drafts do not affect the live calculation or other rules.
  for (const rule of rules) {
    if (!parseGrowerExceptions([rule])) continue;
    for (const name of rule.growers) byGrower.set(growerKey(name), rule);
  }
  return (box: { producer_name?: string | null }, baseRate: number) => {
    const exception = byGrower.get(growerKey(box.producer_name));
    const tariff = exception?.tariff;
    const rate = !tariff
      ? baseRate
      : tariff.mode === 'replace'
        ? tariff.value
        : tariff.mode === 'percentage-discount'
          ? baseRate * (1 - tariff.value / 100)
          : Math.max(0, baseRate - tariff.value);
    return {
      rate,
      baseRate,
      commissionPercentage:
        exception?.commissionPercentage ?? defaultCommission,
      exception: exception ?? null,
    };
  };
}
